import React from 'react';
import { useModalDismiss } from '../hooks/useModalDismiss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faCheck, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Confirmer", 
  cancelText = "Annuler",
  confirmColor = "bg-red-600 hover:bg-red-700",
  icon = faExclamationTriangle,
  loading = false
}) => {
  // Mirrors handleClose: a backdrop dismissal must not fire mid-save.
  const dismiss = useModalDismiss(() => { if (!loading) onClose(); }, { enabled: isOpen && !loading });

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          {...dismiss}
        ></div>

        {/* Modal */}
        <div className="relative inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          <div className="flex items-start">
            <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-red-100">
              <FontAwesomeIcon 
                icon={icon} 
                className="w-6 h-6 text-red-600" 
                aria-hidden="true" 
              />
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-semibold leading-6 text-gray-900">
                {title}
              </h3>
              <div className="mt-2">
                {/* pre-line so callers can lay out multi-line consequences with \n */}
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {message}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-row-reverse gap-3 mt-6">
            <button
              type="button"
              className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg shadow-sm ${confirmColor} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed`}
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin h-4 w-4 mr-2" />
                  Traitement...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} className="h-4 w-4 mr-2" />
                  {confirmText}
                </>
              )}
            </button>
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleClose}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTimes} className="h-4 w-4 mr-2" />
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
